import { useState } from 'react';
import { useAuth } from '../context/AuthContext';

export function HubSpotSettings() {
    const { hubspotConnected, hubspotConfigured, connectHubSpot, disconnectHubSpot, checkHubSpotStatus } = useAuth();
    const [isDisconnecting, setIsDisconnecting] = useState(false);

    const handleDisconnect = async () => {
        if (!confirm('Are you sure you want to disconnect HubSpot? This will stop syncing new bookings to your CRM.')) {
            return;
        }

        setIsDisconnecting(true);
        try {
            await disconnectHubSpot();
            await checkHubSpotStatus();
        } catch (error) {
            console.error('Failed to disconnect HubSpot:', error);
            alert('Failed to disconnect HubSpot. Please try again.');
        } finally {
            setIsDisconnecting(false);
        }
    };

    if (!hubspotConfigured) {
        return (
            <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                    <h2 className="card-title">
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.7 7.3c-.4-.4-.9-.6-1.5-.6h-1.4V5.5c0-1.4-1.1-2.5-2.5-2.5h-2.5c-1.4 0-2.5 1.1-2.5 2.5v1.2H6.8c-.6 0-1.1.2-1.5.6-.4.4-.6.9-.6 1.5v10.4c0 .6.2 1.1.6 1.5.4.4.9.6 1.5.6h10.4c.6 0 1.1-.2 1.5-.6.4-.4.6-.9.6-1.5V8.8c0-.6-.2-1.1-.6-1.5zM9.9 5.5c0-.5.4-.9.9-.9h2.5c.5 0 .9.4.9.9v1.2H9.9V5.5zm8.4 13.7c0 .2-.1.4-.2.5-.1.1-.3.2-.5.2H6.8c-.2 0-.4-.1-.5-.2-.1-.1-.2-.3-.2-.5V8.8c0-.2.1-.4.2-.5.1-.1.3-.2.5-.2h10.4c.2 0 .4.1.5.2.1.1.2.3.2.5v10.4z" fill="currentColor"/>
                            <circle cx="12" cy="14" r="2" fill="currentColor"/>
                        </svg>
                        HubSpot Integration
                    </h2>
                    <p className="text-base-content/70">
                        HubSpot integration is not configured. Contact your administrator to set up the HubSpot app credentials.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
                <h2 className="card-title">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.7 7.3c-.4-.4-.9-.6-1.5-.6h-1.4V5.5c0-1.4-1.1-2.5-2.5-2.5h-2.5c-1.4 0-2.5 1.1-2.5 2.5v1.2H6.8c-.6 0-1.1.2-1.5.6-.4.4-.6.9-.6 1.5v10.4c0 .6.2 1.1.6 1.5.4.4.9.6 1.5.6h10.4c.6 0 1.1-.2 1.5-.6.4-.4.6-.9.6-1.5V8.8c0-.6-.2-1.1-.6-1.5zM9.9 5.5c0-.5.4-.9.9-.9h2.5c.5 0 .9.4.9.9v1.2H9.9V5.5zm8.4 13.7c0 .2-.1.4-.2.5-.1.1-.3.2-.5.2H6.8c-.2 0-.4-.1-.5-.2-.1-.1-.2-.3-.2-.5V8.8c0-.2.1-.4.2-.5.1-.1.3-.2.5-.2h10.4c.2 0 .4.1.5.2.1.1.2.3.2.5v10.4z" fill="currentColor"/>
                        <circle cx="12" cy="14" r="2" fill="currentColor"/>
                    </svg>
                    HubSpot CRM Integration
                </h2>

                {hubspotConnected ? (
                    <>
                        <div className="alert alert-success">
                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h3 className="font-bold">Connected</h3>
                                <div className="text-xs">Your HubSpot account is connected and active</div>
                            </div>
                        </div>

                        <div className="mt-4">
                            <h3 className="font-semibold mb-2">What's synced?</h3>
                            <ul className="list-disc list-inside space-y-1 text-sm text-base-content/70">
                                <li>Contact information (name, email) from direct bookings</li>
                                <li>Meeting engagements logged in HubSpot CRM</li>
                                <li>Meeting notes and messages from requesters</li>
                            </ul>
                        </div>

                        <div className="mt-4">
                            <h3 className="font-semibold mb-2">How to use</h3>
                            <p className="text-sm text-base-content/70">
                                Enable HubSpot sync on individual scheduling links to automatically create contacts and log meetings when bookings are made.
                            </p>
                        </div>

                        <div className="card-actions justify-end mt-4">
                            <button
                                className="btn btn-error btn-sm"
                                onClick={handleDisconnect}
                                disabled={isDisconnecting}
                            >
                                {isDisconnecting ? (
                                    <>
                                        <span className="loading loading-spinner loading-xs"></span>
                                        Disconnecting...
                                    </>
                                ) : (
                                    'Disconnect HubSpot'
                                )}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="text-base-content/70">
                            Connect your HubSpot account to automatically sync contacts and meetings from your scheduling links.
                        </p>

                        <div className="mt-4">
                            <h3 className="font-semibold mb-2">Benefits</h3>
                            <ul className="list-disc list-inside space-y-1 text-sm text-base-content/70">
                                <li>Automatically create or update contacts in HubSpot</li>
                                <li>Log meeting engagements with full details</li>
                                <li>Keep your CRM up-to-date without manual data entry</li>
                                <li>Track all meeting activities in one place</li>
                            </ul>
                        </div>

                        <div className="card-actions justify-end mt-4">
                            <button
                                className="btn btn-primary"
                                onClick={connectHubSpot}
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18.7 7.3c-.4-.4-.9-.6-1.5-.6h-1.4V5.5c0-1.4-1.1-2.5-2.5-2.5h-2.5c-1.4 0-2.5 1.1-2.5 2.5v1.2H6.8c-.6 0-1.1.2-1.5.6-.4.4-.6.9-.6 1.5v10.4c0 .6.2 1.1.6 1.5.4.4.9.6 1.5.6h10.4c.6 0 1.1-.2 1.5-.6.4-.4.6-.9.6-1.5V8.8c0-.6-.2-1.1-.6-1.5zM9.9 5.5c0-.5.4-.9.9-.9h2.5c.5 0 .9.4.9.9v1.2H9.9V5.5zm8.4 13.7c0 .2-.1.4-.2.5-.1.1-.3.2-.5.2H6.8c-.2 0-.4-.1-.5-.2-.1-.1-.2-.3-.2-.5V8.8c0-.2.1-.4.2-.5.1-.1.3-.2.5-.2h10.4c.2 0 .4.1.5.2.1.1.2.3.2.5v10.4z" fill="currentColor"/>
                                    <circle cx="12" cy="14" r="2" fill="currentColor"/>
                                </svg>
                                Connect HubSpot
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
